import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import { ApiService } from '@/services/api';
import type { TMDBMovie, HackerNewsItem, GitHubRepo } from '@/services/api';

interface DiscoverState {
  popularMovies: TMDBMovie[];
  trendingMovies: TMDBMovie[];
  popularLoading: boolean;
  trendingLoading: boolean;
  popularError: string | null;
  trendingError: string | null;
  popularFetchedAt?: number;
  trendingFetchedAt?: number;
}

interface FavoritesState {
  hackerNewsStories: HackerNewsItem[];
  loading: boolean;
  error: string | null;
  fetchedAt?: number;
}

interface ContentState {
  home: {
    trendingRepos: GitHubRepo[];
    loading: boolean;
    error: string | null;
    fetchedAt?: number;
  };
  discover: DiscoverState;
  favorites: FavoritesState;
}

const initialState: ContentState = {
  home: {
    trendingRepos: [],
    loading: false,
    error: null,
  },
  discover: {
    popularMovies: [],
    trendingMovies: [],
    popularLoading: false,
    trendingLoading: false,
    popularError: null,
    trendingError: null,
  },
  favorites: {
    hackerNewsStories: [],
    loading: false,
    error: null,
  },
};

export const fetchPopularMovies = createAsyncThunk('content/fetchPopularMovies', async () => {
  return await ApiService.tmdb.getPopularMovies();
});

export const fetchTrendingRepos = createAsyncThunk('content/fetchTrendingRepos', async () => {
  return await ApiService.github.getTrendingRepos();
});

export const fetchTrendingMovies = createAsyncThunk('content/fetchTrendingMovies', async () => {
  return await ApiService.tmdb.getTrendingMovies();
});

export const fetchHackerNewsStories = createAsyncThunk('content/fetchHackerNewsStories', async () => {
  return await ApiService.hackerNews.getTopStories();
});

const contentSlice = createSlice({
  name: 'content',
  initialState,
  reducers: {
    clearDiscoverErrors: (state) => {
      state.discover.popularError = null;
      state.discover.trendingError = null;
    },
    clearFavoritesError: (state) => {
      state.favorites.error = null;
    },
    clearHomeError: (state) => {
      state.home.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Home trending repos
      .addCase(fetchTrendingRepos.pending, (state) => {
        state.home.loading = true;
        state.home.error = null;
      })
      .addCase(fetchTrendingRepos.fulfilled, (state, action: PayloadAction<GitHubRepo[]>) => {
        state.home.trendingRepos = action.payload;
        state.home.loading = false;
        state.home.fetchedAt = Date.now();
      })
      .addCase(fetchTrendingRepos.rejected, (state, action) => {
        state.home.loading = false;
        state.home.error = action.error.message || '获取GitHub热门仓库失败，请稍后重试';
      })
      // Popular movies
      .addCase(fetchPopularMovies.pending, (state) => {
        state.discover.popularLoading = true;
        state.discover.popularError = null;
      })
      .addCase(fetchPopularMovies.fulfilled, (state, action: PayloadAction<TMDBMovie[]>) => {
        state.discover.popularMovies = action.payload;
        state.discover.popularLoading = false;
        state.discover.popularFetchedAt = Date.now();
      })
      .addCase(fetchPopularMovies.rejected, (state, action) => {
        state.discover.popularLoading = false;
        state.discover.popularError = action.error.message || '获取热门电影失败，请稍后重试';
      })
      // Trending movies
      .addCase(fetchTrendingMovies.pending, (state) => {
        state.discover.trendingLoading = true;
        state.discover.trendingError = null;
      })
      .addCase(fetchTrendingMovies.fulfilled, (state, action: PayloadAction<TMDBMovie[]>) => {
        state.discover.trendingMovies = action.payload;
        state.discover.trendingLoading = false;
        state.discover.trendingFetchedAt = Date.now();
      })
      .addCase(fetchTrendingMovies.rejected, (state, action) => {
        state.discover.trendingLoading = false;
        state.discover.trendingError = action.error.message || '获取趋势电影失败，请稍后重试';
      })
      // Hacker News
      .addCase(fetchHackerNewsStories.pending, (state) => {
        state.favorites.loading = true;
        state.favorites.error = null;
      })
      .addCase(fetchHackerNewsStories.fulfilled, (state, action: PayloadAction<HackerNewsItem[]>) => {
        state.favorites.hackerNewsStories = action.payload;
        state.favorites.loading = false;
        state.favorites.fetchedAt = Date.now();
      })
      .addCase(fetchHackerNewsStories.rejected, (state, action) => {
        state.favorites.loading = false;
        state.favorites.error = action.error.message || '获取Hacker News故事失败，请稍后重试';
      });
  }
});

export const { clearDiscoverErrors, clearFavoritesError, clearHomeError } = contentSlice.actions;
export default contentSlice.reducer;
